import React from "react";
import { Chip, Grid, Divider, Tooltip } from "@mui/material";

import { Error as ErrorIcon } from "@mui/icons-material";
import GridListItem from "components/common/grid/GridListItem";
import usePatientModal from "components/pages/transmissions/modal/patient/usePatientModal";
import HeartFailure from "components/pages/transmissions/modal/patient/tabs/generalInformation/profile/heartFailure/HeatFailure";

import { capitalizeFirstLetter } from "utils/string";

import { USER_ROLES, UCH_CLINICS, CLINIC_NAMES, EVOKE_ROLES } from "static/constants";
import PatientBasicInfoForm from "./PatientBasicInfoForm";
import MagneticProcedures from "./MagneticProcedures";
import LaaoDevice from "./LaaoDevice";
import BenchmarkBillingFlag from "./BenchmarkBillingFlag";
import { isValidArray } from "utils/array";
import { getCurrentUserRole } from "utils/currentUser.util";

function getTitle(titleName) {
  return (
    <>
      <div className="d-flex align-items-center">
        <span className="mr-2">{titleName}</span>
        <span>
          <Tooltip title={`${titleName} is required for ORU`}>
            <ErrorIcon color="error" fontSize="small" />
          </Tooltip>
        </span>
      </div>
    </>
  );
}

const getPhysicianNames = (physicians) => {
  return physicians?.map((physician) => physician?.name)?.join(", ") || "";
};

const PatientBasicInfo = ({
  isEditMode,
  setIsEditMode,
  isLoading,
  handleCancel,
  handleSave,
}) => {
  const {
    currentUser,
    profile,
    newProfileInfo,
    referringProviders,
    newPatientAlertInformation,
  } = usePatientModal();
  const userRole = getCurrentUserRole();

  const { isHl7Exist, account } = newProfileInfo;

  const renderDeactivateReason = (
    <GridListItem
      title="DEACTIVATE REASON"
      subTitle={
        <div className="mr-1 mb-2">{newProfileInfo?.deactivate_reason}</div>
      }
      size="3"
    />
  );

  const renderPatientStatus = (
    <GridListItem
      title="PATIENT STATUS"
      subTitle={
        <Chip
          color={newProfileInfo?.active ? "secondary" : "default"}
          label={
            <span className="text-normal">
              {newProfileInfo?.active ? "ACTIVE" : "INACTIVE"}
            </span>
          }
        />
      }
      size="3"
    />
  );

  return (
    <div className="patient-form__row patient-form__info">
      {isEditMode ? (
        <PatientBasicInfoForm
          handleSave={handleSave}
          setIsEditMode={setIsEditMode}
          isLoading={isLoading}
          handleCancel={handleCancel}
        />
      ) : (
        <Grid container className="view-pdf__drawer-grid">
          <Grid container justifyContent="space-between">
            <GridListItem
              size="3"
              title={
                isHl7Exist && !newProfileInfo.firstName
                  ? getTitle("FIRST NAME")
                  : "FIRST NAME"
              }
              subTitle={newProfileInfo.firstName ?? "—"}
            />
            <GridListItem
              size="3"
              title="MI"
              subTitle={newProfileInfo.middleInitial ?? "—"}
            />
            <GridListItem
              size="3"
              title={
                isHl7Exist && !newProfileInfo.lastName
                  ? getTitle("LAST NAME")
                  : "LAST NAME"
              }
              subTitle={newProfileInfo.lastName ?? "—"}
            />
            <GridListItem
              size="3"
              title="EVOKE ID"
              subTitle={
                newProfileInfo.evokeId ? `#${newProfileInfo.evokeId}` : "—"
              }
              copyValue={newProfileInfo.evokeId}
            />
          </Grid>
          <Grid container justifyContent="space-between">
            <GridListItem
              size="3"
              title={
                isHl7Exist && !newProfileInfo.dob
                  ? getTitle("DATE OF BIRTH")
                  : "DATE OF BIRTH"
              }
              subTitle={newProfileInfo.dob}
              isDate
              copyValue={newProfileInfo.dob}
            />

            <GridListItem
              title={
                isHl7Exist && !newProfileInfo.gender
                  ? getTitle("GENDER (LEGAL)")
                  : "GENDER (LEGAL)"
              }
              size="3"
              subTitle={
                newProfileInfo.gender
                  ? capitalizeFirstLetter(newProfileInfo.gender)
                  : "—"
              }
            />
            <GridListItem
              size="3"
              title="GENDER IDENTITY"
              subTitle={
                newProfileInfo.genderIdentity
                  ? capitalizeFirstLetter(newProfileInfo.genderIdentity)
                  : "—"
              }
            />
            <GridListItem
              title="SEX ASSIGNED AT BIRTH"
              size="3"
              subTitle={
                newProfileInfo.sexAssignedAtBirth
                  ? capitalizeFirstLetter(newProfileInfo.sexAssignedAtBirth)
                  : "—"
              }
            />
          </Grid>
          <Grid container justifyContent="space-between">
            <GridListItem
              size="3"
              title="MARITAL STATUS"
              subTitle={
                newProfileInfo.marital_status
                  ? capitalizeFirstLetter(newProfileInfo.marital_status)
                  : "—"
              }
            />
            <GridListItem
              size="3"
              title="SOCIAL SECURITY NUMBER"
              subTitle={
                newProfileInfo.ssn && newProfileInfo.ssn?.length === 11
                  ? `XXX-XX-${newProfileInfo.ssn?.slice(-4)}`
                  : "—"
              }
            />
            <GridListItem
              size="3"
              title={
                isHl7Exist && !newProfileInfo.doi
                  ? getTitle("DATE OF IMPLANT")
                  : "DATE OF IMPLANT"
              }
              subTitle={newProfileInfo.doi}
              isDate
            />

            <GridListItem
              size="3"
              title={
                isHl7Exist && !newProfileInfo.mrn ? getTitle("MRN") : "MRN"
              }
              subTitle={newProfileInfo.mrn ?? "—"}
              copyValue={newProfileInfo.mrn}
            />
          </Grid>

          <Grid container justifyContent="space-between">
            <GridListItem
              size="3"
              title="ACCOUNT"
              subTitle={newProfileInfo.account ?? "—"}
            />

            <GridListItem
              title={
                account === "Pueblo Cardiology Associates" &&
                isHl7Exist &&
                !getPhysicianNames(newProfileInfo.physicians)
                  ? getTitle("FOLLOWING PHYSICIAN")
                  : "FOLLOWING PHYSICIAN"
              }
              subTitle={getPhysicianNames(newProfileInfo.physicians) ?? "—"}
              size="3"
            />
            <GridListItem
              size="3"
              title="REFERRING PROVIDER"
              subTitle={
                referringProviders?.filter(
                  (referringProvider) =>
                    referringProvider._id === newProfileInfo.referringProviderId
                )?.[0]?.name || "—"
              }
            />
            <GridListItem
              size="3"
              title="HOSPITAL"
              subTitle={newProfileInfo.hospital}
            />
          </Grid>

          <GridListItem
            title="PREFERRED LANGUAGE"
            subTitle={
              isValidArray(newProfileInfo?.preferredLanguage)
                ? newProfileInfo.preferredLanguage.join(", ")
                : "—"
            }
            size="3"
          />
          <GridListItem
            title="PATIENT ALERT"
            subTitle={newPatientAlertInformation.alertPatientText ?? "—"}
            size="3"
          />
          <GridListItem
            title="ENABLE PATIENT ALERT"
            subTitle={
              <Chip
                color={
                  newPatientAlertInformation.alertPatient
                    ? "secondary"
                    : "default"
                }
                label={
                  <span className="d-flex align-items-center text-normal">
                    {newPatientAlertInformation.alertPatient ? "YES" : "NO"}
                  </span>
                }
                style={{ padding: "0px 5px" }}
              />
            }
            size="3"
          />
          {profile?.account === "Centra" &&
            [USER_ROLES.ADMIN, USER_ROLES.CLINIC_ADMIN].includes(
              currentUser.role
            ) && (
              <Grid item xs={3}>
                <HeartFailure />
              </Grid>
            )}

          <MagneticProcedures newProfileInfo={newProfileInfo} />
          <LaaoDevice newProfileInfo={newProfileInfo} />
          <BenchmarkBillingFlag newProfileInfo={newProfileInfo} />
          {EVOKE_ROLES.includes(userRole) && (
            <GridListItem
              title="ALERTS ONLY"
              subTitle={
                <Chip
                  color={newProfileInfo?.alertsOnly ? "secondary" : "default"}
                  label={
                    <span className="d-flex align-items-center text-normal">
                      {newProfileInfo?.alertsOnly ? "Yes" : "No"}
                    </span>
                  }
                  style={{ padding: "0px 5px" }}
                />
              }
              size="3"
            />
          )}
          {!isEditMode && renderPatientStatus}
          {newProfileInfo?.deactivate_reason && renderDeactivateReason}

          <GridListItem
            title="ALERT WHEN DISCONNECTED"
            subTitle={
              <Chip
                color={
                  newProfileInfo?.alertWhenDisconnected
                    ? "secondary"
                    : "default"
                }
                label={
                  <span className="d-flex align-items-center text-normal">
                    {newProfileInfo?.alertWhenDisconnected ? "Yes" : "No"}
                  </span>
                }
                style={{ padding: "0px 5px" }}
              />
            }
            size="3"
          />

          <GridListItem
            title="MONTHLY BATTERY CHECKS"
            subTitle={
              <Chip
                color={
                  newProfileInfo?.monthlyBatteryCheck ? "secondary" : "default"
                }
                label={
                  <span className="d-flex align-items-center text-normal">
                    {newProfileInfo?.monthlyBatteryCheck ? "Yes" : "No"}
                  </span>
                }
                style={{ padding: "0px 5px" }}
              />
            }
            size="3"
          />

          <GridListItem
            title="IN OFFICE ONLY"
            subTitle={
              <Chip
                color={newProfileInfo?.inOfficeOnly ? "secondary" : "default"}
                label={
                  <span className="d-flex align-items-center text-normal">
                    {newProfileInfo?.inOfficeOnly ? "Yes" : "No"}
                  </span>
                }
                style={{ padding: "0px 5px" }}
              />
            }
            size="3"
          />

          {newProfileInfo?.account === CLINIC_NAMES.PUEBLO && (
            <GridListItem
              title="STATUS LETTER"
              subTitle={
                <Chip
                  color={newProfileInfo?.statusLetter ? "secondary" : "default"}
                  label={
                    <span className="d-flex align-items-center text-normal">
                      {newProfileInfo?.statusLetter ? "Yes" : "No"}
                    </span>
                  }
                  style={{ padding: "0px 5px" }}
                />
              }
              size="3"
            />
          )}

          {[
            USER_ROLES.USER,
            USER_ROLES.ADMIN,
            USER_ROLES.CLINIC_ADMIN,
          ].includes(currentUser?.role) &&
            UCH_CLINICS.includes(profile?.account) && (
              <>
                <Divider
                  style={{ height: "1px", width: "100%", marginBottom: "10px" }}
                />

                <Grid container xs={12} flexDirection="column">
                  <div className="patient-form__info--header mb-0">
                    Process HF Report Direct To Clinic
                  </div>
                  <div className="text-normal text-grey mb-2">
                    Toggle to select HF report should be transmitted to the
                    clinic
                  </div>

                  <Grid container>
                    <Grid item xs={3} className="d-flex align-items-center">
                      <div className="text-normal text-grey mr-2">HF</div>
                      <Chip
                        color={
                          newProfileInfo?.hfToClinic ? "secondary" : "default"
                        }
                        label={
                          <span className="text-normal">
                            {newProfileInfo?.hfToClinic ? "YES" : "NO"}
                          </span>
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
        </Grid>
      )}
    </div>
  );
};

export default PatientBasicInfo;
